import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { api } from '../api/client';
import { useLang } from '../i18n/lang';
import { G } from '../i18n/glossary';

// Combined sign-in / first-run admin setup / employee self-registration screen.
export function AuthScreen() {
  const { needsSetup, setUser } = useAuth();
  const { t } = useLang();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [regOpen, setRegOpen] = useState(false);
  const [domain, setDomain] = useState('@3dtsi.com');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('estimator');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  // Is self-registration turned on? (only relevant once setup is done)
  useEffect(() => {
    if (needsSetup) return;
    api.registrationStatus().then((s) => { setRegOpen(s.open); setDomain(s.domain); }).catch(() => setRegOpen(false));
  }, [needsSetup]);

  const registering = !needsSetup && mode === 'register';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(''); setNotice('');
    setBusy(true);
    try {
      if (registering) {
        // New self-registrations are pending until an admin approves them.
        const res = await api.register({ email, name, password, role });
        setMode('login');
        setNotice(res.message || t({ en: 'Account created — an admin must approve it before you can sign in.', fil: 'Nagawa na ang account — kailangan munang aprubahan ito ng Admin bago ka makakapag-sign in.' }));
        setPassword('');
        return;
      }
      const user = needsSetup
        ? await api.setup({ email, name, password })
        : await api.login({ email, password });
      setUser(user);
    } catch (err) {
      setError(err instanceof Error ? err.message : t({ en: 'Something went wrong', fil: 'May nangyaring mali' }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-wrap">
      <div className="card auth-card">
        <img src="/logo-white.png" alt="The AI Solution for 3D" className="auth-logo" />
        <h1>{needsSetup ? t({ en: 'Create admin account', fil: 'Gumawa ng admin account' }) : registering ? t({ en: 'Create your account', fil: 'Gumawa ng iyong account' }) : t(G.signIn)}</h1>
        <p className="muted" style={{ marginTop: 0 }}>
          {needsSetup
            ? t({ en: 'First run — set up the administrator for The AI Solution.', fil: 'Unang takbo — i-set up ang administrator para sa The AI Solution.' })
            : registering
              ? t({ en: `3DTSI employees only — register with your ${domain} email.`, fil: `Para sa mga empleyado ng 3DTSI lamang — mag-register gamit ang iyong ${domain} email.` })
              : 'The AI Solution · 3DTSI Estimating'}
        </p>
        {needsSetup && <div className="banner">{t({ en: 'No users exist yet. The account you create now becomes the Admin.', fil: 'Wala pang users. Ang account na gagawin mo ngayon ang magiging Admin.' })}</div>}
        {notice && <div className="banner" style={{ borderColor: 'var(--teal)' }}>{notice}</div>}
        <form onSubmit={submit}>
          {(needsSetup || registering) && (
            <div className="field">
              <label htmlFor="name">{t({ en: 'Full name', fil: 'Buong pangalan' })}</label>
              <input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
          )}
          <div className="field">
            <label htmlFor="email">{t(G.email)}{registering ? ` (${domain})` : ''}</label>
            <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          {registering && (
            <div className="field">
              <label htmlFor="role">{t(G.role)}</label>
              <select id="role" value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="estimator">Estimator</option>
                <option value="salesperson">Salesperson</option>
              </select>
            </div>
          )}
          <div className="field">
            <label htmlFor="password">{t(G.password)}{(needsSetup || registering) ? t({ en: ' (8+ characters)', fil: ' (8+ na karakter)' }) : ''}</label>
            <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              minLength={8} required />
          </div>
          {error && <div className="error">{error}</div>}
          <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%', justifyContent: 'center' }}>
            {busy ? t({ en: 'Please wait…', fil: 'Sandali lang…' }) : needsSetup ? t({ en: 'Create admin & continue', fil: 'Gumawa ng Admin at magpatuloy' }) : registering ? t({ en: 'Create account & continue', fil: 'Gumawa ng account at magpatuloy' }) : t(G.signIn)}
          </button>
        </form>
        {!needsSetup && regOpen && (
          <p className="muted" style={{ fontSize: 13, textAlign: 'center', marginBottom: 0, marginTop: 14 }}>
            {registering
              ? <>{t({ en: 'Already have an account?', fil: 'May account ka na?' })} <button className="link-btn" onClick={() => { setMode('login'); setError(''); }}>{t(G.signIn)}</button></>
              : <>{t({ en: 'New 3DTSI employee?', fil: 'Bagong empleyado ng 3DTSI?' })} <button className="link-btn" onClick={() => { setMode('register'); setError(''); }}>{t({ en: 'Create an account', fil: 'Gumawa ng account' })}</button></>}
          </p>
        )}
      </div>
    </div>
  );
}
